import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Activity, ArrowLeft, CalendarClock, History } from "lucide-react"
import { useCallback, useState } from "react"
import {
	ContestDatesForm,
	type ContestDatesFormValues,
} from "@/components/contests/contest-dates-form"
import { ContestStatusBadge } from "@/components/contests/contest-status-badge"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	Section,
} from "@/components/layout"
import {
	DetailColumn,
	DetailLayout,
	DetailPage,
	DetailPageHeader,
} from "@/components/promotions/detail-page"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ContestActions } from "@/features/contests-admin/ContestActions"
import { ContestDefinitionPanel } from "@/features/contests-admin/ContestDefinitionPanel"
import { ContestStatusMarks } from "@/features/contests-admin/ContestOverview"
import { ContestRefereesGrid } from "@/features/contests-admin/ContestRefereesGrid"
import { ContestSpecialEventsCard } from "@/features/contests-admin/ContestSpecialEventsCard"
import { ContestStandingsGrid } from "@/features/contests-admin/ContestStandingsGrid"
import {
	type AdminContest,
	formatInstant,
} from "@/features/contests-admin/types"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import {
	useContest,
	useContestReferees,
	useContestStandings,
	useUpsertContestDates,
} from "@/queries/marketplace"

export const Route = createFileRoute("/contests/$contestId/")({
	component: ContestDetailPage,
})

function ContestDatesCard({ contest }: { contest: AdminContest }) {
	const upsertMutation = useUpsertContestDates()
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceCoupons)

	const handleSubmit = ({ startsAt, endsAt }: ContestDatesFormValues) => {
		upsertMutation.mutate({ id: contest.id, startsAt, endsAt })
	}

	return (
		<DetailPanel title="Dates" icon={CalendarClock}>
			<DetailPanelSection>
				<ContestDatesForm
					key={`${contest.startsAt}|${contest.endsAt}`}
					contestId={contest.id}
					initialValues={{
						startsAt: contest.startsAt,
						endsAt: contest.endsAt,
					}}
					onSubmit={handleSubmit}
					isSubmitting={upsertMutation.isPending}
					disabled={!canOperate || contest.settledAt !== null}
					disabledReason={
						canOperate
							? undefined
							: "Editing the dates needs the marketplace coupons role."
					}
					startLocked={contest.status !== "upcoming"}
				/>
			</DetailPanelSection>
		</DetailPanel>
	)
}

function ContestDetailPage() {
	const { contestId } = Route.useParams()
	const { contest, definition, specialEvents, isLoading, error } = useContest({
		contestId,
	})
	const [standingsPage, setStandingsPage] = useState(1)
	const [standingsLimit, setStandingsLimit] = useState(20)
	const [refereesPage, setRefereesPage] = useState(1)
	const [refereesLimit, setRefereesLimit] = useState(20)
	const standingsQuery = useContestStandings({
		contestId,
		page: standingsPage,
		limit: standingsLimit,
	})
	const refereesQuery = useContestReferees({
		contestId,
		page: refereesPage,
		limit: refereesLimit,
	})

	const handleStandingsPagination = useCallback(
		(page: number, limit: number) => {
			setStandingsPage(page)
			setStandingsLimit(limit)
		},
		[],
	)
	const handleRefereesPagination = useCallback(
		(page: number, limit: number) => {
			setRefereesPage(page)
			setRefereesLimit(limit)
		},
		[],
	)

	if (isLoading) {
		return (
			<DetailPage>
				<Skeleton className="h-8 w-64" />
				<DetailLayout>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
					<Skeleton className="h-72" />
				</DetailLayout>
			</DetailPage>
		)
	}

	if (error || !contest) {
		return (
			<DetailPage className="items-start gap-3">
				<p className="text-bad">
					{error?.message || "The contest could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/contests">
						<ArrowLeft />
						Back to contests
					</Link>
				</Button>
			</DetailPage>
		)
	}

	return (
		<DetailPage>
			<DetailPageHeader
				title={<span className="font-mono break-all">{contest.id}</span>}
				badges={<ContestStatusBadge contest={contest} />}
				description={
					<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
						{contest.definitionId !== contest.id ? (
							<>
								<span>
									Rules:{" "}
									<span className="font-mono text-foreground">
										{contest.definitionId}
									</span>
								</span>
								<span className="text-meta">·</span>
							</>
						) : null}
						<span className="tabular-nums">
							{formatInstant(contest.startsAt)} to{" "}
							{formatInstant(contest.endsAt)}
						</span>
					</span>
				}
			/>

			<DetailLayout>
				<DetailColumn>
					<ContestActions contest={contest} />

					<ContestDatesCard contest={contest} />

					<ContestSpecialEventsCard
						contest={contest}
						specialEvents={specialEvents}
					/>

					<Section label={`Standings · ${standingsQuery.total}`}>
						{standingsQuery.error ? (
							<p className="text-bad">{standingsQuery.error.message}</p>
						) : (
							<ContestStandingsGrid
								contestId={contest.id}
								standings={standingsQuery.standings}
								isLoading={standingsQuery.isLoading}
								page={standingsPage}
								pageSize={standingsLimit}
								total={standingsQuery.total}
								totalPages={standingsQuery.totalPages}
								onPaginationChange={handleStandingsPagination}
							/>
						)}
					</Section>

					<Section label={`Referred accounts · ${refereesQuery.total}`}>
						{refereesQuery.error ? (
							<p className="text-bad">{refereesQuery.error.message}</p>
						) : (
							<ContestRefereesGrid
								referees={refereesQuery.referees}
								isLoading={refereesQuery.isLoading}
								page={refereesPage}
								pageSize={refereesLimit}
								total={refereesQuery.total}
								totalPages={refereesQuery.totalPages}
								onPaginationChange={handleRefereesPagination}
							/>
						)}
					</Section>

					<ContestDefinitionPanel definition={definition} />
				</DetailColumn>

				<DetailColumn>
					<DetailPanel title="Activity" icon={Activity}>
						<DetailPanelSection>
							<KeyValueList>
								<KeyValueItem label="Participants">
									{contest.participants}
								</KeyValueItem>
								<KeyValueItem label="Referees">
									{contest.refereeTotal}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
					</DetailPanel>
					<DetailPanel title="Lifecycle" icon={History}>
						<DetailPanelSection>
							<ContestStatusMarks contest={contest} />
						</DetailPanelSection>
					</DetailPanel>
				</DetailColumn>
			</DetailLayout>
		</DetailPage>
	)
}
